import { useState } from 'react'
import { toast } from 'react-toastify'
import T from 'T'
import { useUpdateConfiguredServiceMutation } from 'api/configured-service/configuredServiceCrud'
import { isYearlyRecurrence } from 'utils/service'
import { findRecurrenceOptionByKey } from '../utils/recurrenceOptionsHelper'
import { EVENT_INSTANCE_TYPES } from 'settings/constants/service'

/**
 * Custom hook for handling form submission in EditServiceRecurrenceDialog
 * Manages the update process, error handling, and toast notifications
 */
export const useFormSubmission = (
  methods,
  serviceFormDefaults,
  recurrenceOptions,
  formatFormData,
  onClose,
  onRecurrenceUpdate
) => {
  const [error, setError] = useState(null)
  const [updateConfiguredService, { isLoading: isUpdating }] = useUpdateConfiguredServiceMutation()

  const handleSubmit = async () => {
    setError(null)
    try {
      const formValues = methods.getValues()

      // Convert serviceInterval string back to object for submission
      const serviceIntervalObj = findRecurrenceOptionByKey(recurrenceOptions, formValues.serviceInterval) || serviceFormDefaults.recurrence

      // Merge only the updated recurrence fields with service defaults
      const updateData = {
        ...serviceFormDefaults,
        recurrenceFrequency: +formValues.recurrenceFrequency,
        recurrence: serviceIntervalObj,
        recurrenceInterval: serviceIntervalObj?.recurrenceInterval,
        daysOfWeek: formValues.daysOfWeek,
        eventInstanceType: formValues.eventInstanceType,
        eventOccurrence: formValues.eventOccurrence,
        dayOfTheWeek: formValues.dayOfTheWeek,
        dayOfTheMonth: formValues.dayOfTheMonth,
      }

      // If Yearly is selected, enforce defaults for hidden fields
      if (isYearlyRecurrence(serviceIntervalObj?.recurrenceInterval)) {
        const frequency = +formValues.recurrenceFrequency || 1
        updateData.eventOccurrence = Array(frequency).fill(1)
        updateData.dayOfTheWeek = Array(frequency).fill('Monday')
        updateData.eventInstanceType = EVENT_INSTANCE_TYPES.DAY_OF_WEEK
      }

      const formattedData = formatFormData(updateData)

      // Extract only recurrence fields to update parent form
      if (onRecurrenceUpdate) {
        const recurrenceOnlyUpdate = {
          recurrenceFrequency: updateData.recurrenceFrequency,
          recurrence: updateData.recurrence,
          recurrenceInterval: updateData.recurrenceInterval,
          daysOfWeek: updateData.daysOfWeek,
          eventInstanceType: updateData.eventInstanceType,
          eventOccurrence: updateData.eventOccurrence,
          dayOfTheWeek: updateData.dayOfTheWeek,
          dayOfTheMonth: updateData.dayOfTheMonth,
        }
        onRecurrenceUpdate(recurrenceOnlyUpdate)
      }

      // Submit update with toast notifications
      const updatePromise = updateConfiguredService(formattedData)
        .unwrap()
        .then(() => onClose())

      toast.promise(updatePromise, {
        pending: {
          render() {
            return T.UPDATING_RECURRENCE
          },
          icon: true,
        },
        success: {
          render() {
            return T.RECURRENCE_UPDATED_SUCCESSFULLY
          },
          icon: true,
        },
        error: {
          render({ data }) {
            return `${T.FAILED_TO_UPDATE_RECURRENCE}: ${data?.message || 'Unknown error'}`
          },
          icon: true,
        },
      })
    } catch (err) {
      setError(err?.message || T.ERROR_UPDATING_RECURRENCE)
    }
  }

  return {
    handleSubmit,
    error,
    setError,
    isUpdating,
  }
}
